/**
 * Compact encoding for filterCriteria saved in views.
 *
 * Instead of storing the full parsed JS object (which includes verbose *Labels and
 * *Colors companion keys for every dropdown filter), we re-encode as a compact
 * query string containing only the ID values. Labels and colors are reconstructed
 * on load by the filter UI's existing option-lookup logic in formatForForm().
 *
 * Reserved keys inside filterCriteria:
 *   _fc  — filter criteria query string (this module)
 *   _ts  — table column settings (tableSettings.ts)
 *   _s   — sort column id
 *   _o   — sort order (ASC/DESC)
 *
 * All reserved keys are stripped before URL params are applied on load.
 */

/**
 * Keys that are handled separately or should never appear inside _fc.
 * sort/order are stored in their own _s/_o keys to keep filter data clean.
 */
const OMIT_KEYS = new Set([
  '_fc',
  '_ts',
  '_s',
  '_o',
  'taskId',
  'viewId',
  'page',
  'cursor',
  'sort',
  'order',
]);

/** Matches keys that are purely display companions: brandLabels, brandColors, cf_42_ddLabels, etc. */
const isLabelOrColorKey = (key: string): boolean =>
  key.endsWith('Labels') || key.endsWith('Colors');

/**
 * Serializes a filterCriteria object to a compact query string, dropping
 * all *Labels/*Colors companion keys and sort/order (stored separately as _s/_o).
 *
 * Values are converted to strings using the same rules as the URL encoding
 * in ViewDropdown (numbers → "n|<n>", objects → "o|<json>", rest → string).
 *
 * Example output: "brand=12,34&lastUpdated=2024-01-01,2025-01-01&cf_42_dd=7,8"
 */
export const serializeFilterCriteria = (filterCriteria: Record<string, unknown>): string => {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(filterCriteria)) {
    if (OMIT_KEYS.has(key)) continue;
    if (isLabelOrColorKey(key)) continue;
    if (value == null || value === '') continue;

    let strValue: string;
    if (typeof value === 'number') {
      strValue = `n|${value}`;
    } else if (typeof value === 'object') {
      try {
        strValue = `o|${JSON.stringify(value)}`;
      } catch {
        continue;
      }
    } else {
      strValue = String(value);
    }

    if (strValue !== '') {
      params.set(key, strValue);
    }
  }

  return params.toString();
};

/**
 * Deserializes a compact query string back to a Record<string, string>
 * suitable for passing to setSearchParams.
 *
 * Labels/Colors are intentionally absent — the filter UI's formatForForm()
 * reconstructs display labels from option lists automatically.
 */
export const deserializeFilterCriteria = (str: string): Record<string, string> => {
  if (!str) return {};

  const result: Record<string, string> = {};
  const params = new URLSearchParams(str);

  params.forEach((value, key) => {
    if (value !== '') {
      result[key] = value;
    }
  });

  return result;
};
